<template>
  <VSSelectBase
    :id="props.id"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :label="props.label"
    :label-tooltip="props.labelTooltip"
    :description="props.description"
    :open="props.open"
    :name="props.name"
    :search="props.search"
    :theme="props.theme"
    :allow-empty="props.allowEmpty"
    :actions="props.actions"
    :options-provider="optionsProvider"
    :options-ensured="modelValue ? [modelValue] : []"
    :validation-result="validationResult"
    data-component-name="VSelect"
    :is-selected="isSelected"
    :is-any-selected="isAnySelected"
    :alt="props.alt"
    @internal:select="select"
    @internal:add="add"
    @internal:set="set"
  >
    <template v-if="slots.prefix" #prefix>
      <slot name="prefix" />
    </template>
    <template v-if="slots.actions" #actions>
      <slot name="actions" />
    </template>
    <slot name="display">
      <template v-if="selectedOption">
        <VIcon v-if="selectedOption.icon" dense class="mr-2" :name="selectedOption.icon" />
        <VTooltip :content="String(selectedOption.label ?? selectedOption.value)">
          <div class="truncate">{{ selectedOption.label ?? selectedOption.value }}</div>
        </VTooltip>
      </template>
      <template v-else>
        <VTooltip :content="props.placeholder">
          <div class="truncate text-neutral-500" :class="{ 'text-opacity-50': props.disabled }" v-text="props.placeholder || '&nbsp;'" />
        </VTooltip>
      </template>
    </slot>
  </VSSelectBase>
</template>
<script lang="ts" generic="T" setup>
import { computed } from 'vue';
import VIcon from './../labels/VIcon.vue'
import VSSelectBase from './components/VSSelectBase.vue'
import { type VSSelectBaseProps } from './helpers/select';
import type { V } from '@utils/types';
import { equal } from '@avvoka/shared';
import { createDefaultValidator, useValidation } from './helpers/validations';
import type { ValidationProps } from './helpers/types';
import { isNullOrUndefined } from '@utils/validations';
import { toSelectOptionsProvider } from './helpers/selectOptions';
import VTooltip from '@library/dialogs/VTooltip.vue';

defineOptions({
  name: 'VSelect'
})

const props = defineProps<VSSelectBaseProps<T> & ValidationProps<T>>()

const slots = defineSlots<{
  prefix?: () => unknown
  display?: () => unknown
  actions?: () => unknown
}>()

const modelValue = defineModel<undefined | null | T>({ required: true })

const { validationVisible, validationResult, isValid } = useValidation(
  modelValue,
  props,
  createDefaultValidator(props)
)

defineExpose({
  isValid,
  get isEmpty () {
    return !isAnySelected.value
  }
})

const isSelected = (option: V.Select.Item<T>) => equal(modelValue.value, option.value)

const optionsProvider = toSelectOptionsProvider(computed(() => props.options)).use()

const selectedOption = computed(() => optionsProvider.findItemByValue(modelValue.value) ?? (modelValue.value && optionsProvider.fetchItemByValue(modelValue.value)))

const isAnySelected = computed(() => !isNullOrUndefined(modelValue.value))

const select = (value: T) => {
  modelValue.value = value

  validationVisible.value = true
}

const add = (value: T) => {
  modelValue.value = value
}

const set = (values: T[], action: 'set' | 'select' | 'unselect') => {
  if (action === 'set') {
    modelValue.value = values.length === 0 ? null : values[0]
  }

  validationVisible.value = true
}
</script>